// @flow
import React from 'react'
import styled, { keyframes } from 'styled-components'
import i18n from 'live/services/i18n'
import FullHeightSelector from 'live/components/ui/full-height-selector/index'
import {
    FullHeightSelectorOpenContainer,
    OptionBackground
} from 'live/components/ui/full-height-selector/full-height-selector-open'
import { FullHeightSelectorClosedContainer } from 'live/components/ui/full-height-selector/full-height-selector-closed'
import _ from 'lodash'
import { getAssetType, capitalizedFirstLetter } from 'plugin-assets/shared/util'
import ChannelIcon from '../../components/channel-icon'
import { useService } from 'live/services/service-locator'
import { AssetTestFilter } from 'plugin-assets/shared/widget/components/AssetTestFilter'
import channelsData, {
    ChannelsDataContext,
    getLookupAssetConfig
} from 'plugin-assets/shared/widget/lib/hocs/channels-data'
import RadioMenu from 'plugin-assets/shared/components/RadioMenu'
import { AssetSelectionOptions, AssetSelectionOptionsKeys } from 'plugin-assets/shared/widget/lib/util'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

type Props = {
    changeLayoutConfig: (string, any) => void
    lockAssetSelector: boolean
    hidden: boolean
} & ChannelsData &
    ChannelsValueTypeActionProps
// ChannelsConfig receives props from channelsData HOC

class ChannelsConfig extends React.Component<Props> {
    static contextType = ChannelsDataContext

    config = (): ChannelsValueJsonConfig => this.props.widgetConfig
    toggleValueBold = (): void => this.props.changeLayoutConfig('valueBold', !this.config().valueBold)
    toggleUnitBold = (): void => this.props.changeLayoutConfig('unitBold', !this.config().unitBold)
    toggleUnitItalic = (): void => this.props.changeLayoutConfig('unitItalic', !this.config().unitItalic)
    toggleValueItalic = (): void => this.props.changeLayoutConfig('valueItalic', !this.config().valueItalic)
    changeValueFontColor = (value: string): void => this.props.changeLayoutConfig('valueFontColor', value)
    changeUnitFontColor = (value: string): void => this.props.changeLayoutConfig('unitFontColor', value)
    changeValueFontColorOption = (value: string): void => this.props.changeLayoutConfig('valueFontColorOption', value)
    changeUnitFontColorOption = (value: string): void => this.props.changeLayoutConfig('unitFontColorOption', value)
    changeUnitProportion = (value: string): void => this.props.changeLayoutConfig('unitProportion', value)

    onChangeSelectionMode = (selectionMode: string): void => {
        this.props.changeLayoutConfig('assetSelectionMode', selectionMode)
    }

    changeChannels = (channel: LiveSelectChannelOption): void => {
        const selectedAsset = this.props.selectedAssets[0]
        if (selectedAsset) {
            if (channel) this.props.onChangeAssetChannels(selectedAsset, [channel])
            else this.props.onChangeAssetChannels(selectedAsset, [])
        } else {
            console.error('No asset selected')
        }
    }

    changeAsset = (asset: LiveSelectAssetOption): void => {
        const firstChannel = _.get(this.props, 'assetsConfig[0].channels[0]', null)
        if (asset) {
            this.props.setSelectedAssets(
                [asset],
                [
                    {
                        ...this.props.createAssetConfig(asset),
                        channels: firstChannel ? [firstChannel] : []
                    }
                ]
            )
        } else if (
            this.getSelectionModeStatus(this.props.selectedAssets[0], this.props.widgetConfig) ===
            AssetSelectionOptions.selectedByFilter
        ) {
            this.props.setSelectedAssets([])
        }
    }

    renderAssetInfo = (asset: LiveSelectAssetOption): React.ReactElement => (
        <AssetInfo data-testid={`asset-info-${asset.value}`}>{_.get(asset, 'data.type', '').toUpperCase()}</AssetInfo>
    )

    renderAssetEmptyState = (): React.ReactElement => {
        return <EmptyState />
    }

    renderChannelEmptyState = (): React.ReactElement => {
        return (
            <AssetEmptyState>
                <EmptyStateIconContainer>
                    <ChannelIcon />
                </EmptyStateIconContainer>
                {i18n('NO CHANNELS AVAILABLE')}
            </AssetEmptyState>
        )
    }

    onToggleAssetLookup = (): void => this.changeAsset(getLookupAssetConfig())

    getSelectionModeStatus = (selectedAsset, widgetConfig): AssetSelectionOptionsKeys => {
        if (selectedAsset?.data?.lookup) {
            return AssetSelectionOptions.selectedByFilter
        } else if (widgetConfig.canChangeAssetOnVisualization) {
            return AssetSelectionOptions.editableOnViewMode
        }

        return this.props.widgetConfig.assetSelectionMode
    }

    // These methods are necessary so that we don't lose pre-existing configurations created when we used toggle buttons
    // TODO: Deprecate in 4.0
    updateCanChangeAsset(AssetSelectionOptions: any): void {
        if (this.props.widgetConfig.assetSelectionMode === AssetSelectionOptions.editableOnViewMode) {
            this.props.changeLayoutConfig('canChangeAssetOnVisualization', true)
        }
    }

    componentDidMount(): void {
        this.updateCanChangeAsset(AssetSelectionOptions)
    }

    render(): JSX.Element {
        const widgetConfig = this.config()
        const assetSelectionType = this.props.widgetConfig.assetSelectionMode
        const selectedAsset = _.get(this.props, 'selectedAssets[0]', null)
        const canChangeAssetOnVisualization = this.props.widgetConfig.canChangeAssetOnVisualization
        const hasLookupData = !!selectedAsset?.data?.lookup

        const selectedChannel =
            _.get(this.props, 'assetsConfig[0].channels[0]', null) ||
            _.get(this.props, 'assetsConfig[0].calculatedChannels[0]')

        // If there is only one asset type, we will use it as the asset name, otherwise we will use "Assets"
        const assetType = getAssetType(this.props.assets)
        const assets = [...this.props.assets]

        return (
            <ChannelsConfigContainer hidden={this.props.hidden}>
                {this.props.loadingAssets ? (
                    <Loading className="animate-flicker">{i18n('Loading channels..')}</Loading>
                ) : (
                    <AssetRelativeContainer isEditor={this.props.isEditor}>
                        {this.props.isEditor && (
                            <RadioMenu
                                widgetConfig={widgetConfig}
                                selectedAsset={selectedAsset}
                                getSelectionModeStatus={this.getSelectionModeStatus}
                                changeAsset={this.changeAsset}
                                changeLayoutConfig={this.props.changeLayoutConfig}
                            />
                        )}
                        {!hasLookupData &&
                        (this.props.isEditor ||
                            assetSelectionType === AssetSelectionOptions.editableOnViewMode ||
                            canChangeAssetOnVisualization) ? (
                            <FullHeightSelector
                                selectedOption={selectedAsset}
                                loading={this.props.loadingAssets}
                                title={i18n('Choose asset')}
                                selectTitle={i18n('Choose asset')}
                                options={assets}
                                onChange={this.changeAsset}
                                renderOptionInfo={this.renderAssetInfo}
                                renderEmptyState={this.renderAssetEmptyState}
                            />
                        ) : null}
                        {(this.props.isEditor && assetSelectionType === AssetSelectionOptions.selectedByFilter) ||
                        hasLookupData ? (
                            <StyledAssetTestFilter assetOption={selectedAsset} />
                        ) : null}
                        {!this.props.isEditor &&
                        (assetSelectionType === AssetSelectionOptions.selectedByFilter || hasLookupData) ? (
                            <React.Fragment>
                                <TitleBlockedAsset>{i18n(capitalizedFirstLetter(assetType))}</TitleBlockedAsset>
                                <LabelBlockedAsset>
                                    {i18n(capitalizedFirstLetter(selectedAsset?.label))}
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                            <Tooltip id="repetititon_window">
                                                {i18n("Use the dashboard's filter to select the asset")}
                                            </Tooltip>
                                        }
                                    >
                                        <i className="fa fa-filter" />
                                    </OverlayTrigger>
                                </LabelBlockedAsset>
                            </React.Fragment>
                        ) : (!this.props.isEditor &&
                              assetSelectionType === AssetSelectionOptions.notEditableOnViewMode) ||
                          (!this.props.isEditor && !canChangeAssetOnVisualization) ? (
                            <React.Fragment>
                                <TitleBlockedAsset>{i18n(capitalizedFirstLetter(assetType))}</TitleBlockedAsset>
                                <LabelBlockedAsset>
                                    {i18n(capitalizedFirstLetter(selectedAsset?.label))}
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                            <Tooltip id="repetititon_window">
                                                {i18n('Asset not editable on view mode')}
                                            </Tooltip>
                                        }
                                    >
                                        <i className="fa fa-lock"></i>
                                    </OverlayTrigger>
                                </LabelBlockedAsset>
                            </React.Fragment>
                        ) : null}
                        {this.props.isEditor && selectedAsset === null ? (
                            <React.Fragment>
                                <TitleBlockedChannel>
                                    {i18n('Choose channel')}
                                    <LabelBlockedChannel>
                                        {i18n('Type to search channel')}
                                        <i className="fas fa-search"></i>
                                    </LabelBlockedChannel>
                                </TitleBlockedChannel>
                            </React.Fragment>
                        ) : (
                            <ChannelRelativeContainer>
                                <FullHeightSelector
                                    selectedOption={selectedChannel}
                                    loading={this.props.loadingChannels}
                                    title={i18n('Choose channel')}
                                    selectTitle={i18n('Choose channel')}
                                    options={this.props.channels}
                                    onChange={this.changeChannels}
                                    renderEmptyState={this.renderChannelEmptyState}
                                />
                            </ChannelRelativeContainer>
                        )}
                    </AssetRelativeContainer>
                )}
            </ChannelsConfigContainer>
        )
    }
}

function EmptyState(): React.ReactElement {
    const assetService = useService('asset-service')

    return (
        <AssetEmptyState>
            {assetService?.types?.Consumer ? (
                <assetService.types.Consumer>
                    {items => {
                        const AssetIcon = items.length === 1 ? items[0].Icon : () => null
                        const assetType = items.length === 1 ? items[0].name : 'asset'

                        return (
                            <React.Fragment>
                                <EmptyStateIconContainer>
                                    <AssetIcon />
                                </EmptyStateIconContainer>
                                {`${i18n('blank-state-NO')}${i18n(assetType).toUpperCase()}${i18n(
                                    'blank-state-AVAILABLE'
                                )}`}
                            </React.Fragment>
                        )
                    }}
                </assetService.types.Consumer>
            ) : null}
        </AssetEmptyState>
    )
}

const keyFrameLoadingAnimation = keyframes`
  0% {
    opacity:1;
  }
  50%{
    opacity:0.2;
  }
  100% {
    opacity:1;
  }
`
const ChannelsConfigContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 0 auto;
    ${props => (props.hidden ? 'display: none;' : '')}
`

const Loading = styled.div`
    font-size: 15px;
    text-align: center;
    padding: 13px 10px 0px 10px;
    font-family: ${({ theme }) => theme['font-regular']};
    animation: ${keyFrameLoadingAnimation} 1s ease-in-out 0s infinite;
`
const padding = '13px 10px 0px 10px'

const AssetRelativeContainer = styled.div<{ isEditor: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-bottom: 17px;
    ${FullHeightSelectorClosedContainer} {
        padding: ${padding};
        div {
            background-color: ${({ isEditor }) => (isEditor ? 'var(--gray-0)' : 'var(--static-gray-900)')};
            color: ${({ isEditor }) => (isEditor ? 'var(--blue-800)' : 'var(--static-gray-100)')};
            border-color: ${({ isEditor }) => (isEditor ? 'var(--gray-400)' : '#3f4759')};
        }

        label {
            color: var(--gray-800);
        }
    }
    ${FullHeightSelectorOpenContainer} {
        padding: ${padding};
        height: fit-content;
        max-height: 250px;
        position: relative;
        ${OptionBackground} {
            background-color: ${({ isEditor }) => (isEditor ? 'var(--gray-0)' : 'var(--static-gray-1000)')};
            color: ${({ isEditor }) => (isEditor ? 'var(--blue-800)' : 'var(--static-gray-0)')};
            border-color: ${({ isEditor }) => (isEditor ? 'var(--gray-400)' : '#3f4759')};
            :hover {
                background-color: ${({ isEditor }) => (isEditor ? 'var(--blue-200)' : '#17324D')};
            }
        }
    }
    > ${FullHeightSelectorOpenContainer} {
        /* keeping the asset selector on top of the channel selector */
        z-index: 101002;
        position: relative;
    }
`
const StyledAssetTestFilter = styled(AssetTestFilter)`
    padding: ${padding};
    z-index: 101003;
    .help-tip {
        margin-left: 272px;
        margin-top: 2px;
    }

    div {
        color: ${({ theme }) => (theme.themeName === 'light' ? 'var(--gray-800)' : 'var(--static-gray-300)')};
    }
`
const TitleBlockedAsset = styled.label`
    margin-left: 15px;
    margin-top: 10px;
    margin-bottom: 1px;
`

export const LabelBlockedAsset = styled.div`
    font-size: 13px;
    line-height: 13px;
    display: flex;
    width: 94%;
    height: 36px;
    padding: 10px;
    border-radius: 3px;
    border: 1px solid ${({ theme }) => theme['darker-borders']};
    color: ${({ theme }) => theme['input-borders']};
    margin-left: 10px;
    position: relative;
    i {
        margin-left: 87%;
        position: absolute;
    }
`
export const TitleBlockedChannel = styled.div`
    padding: 13px 10px 0px;
    position: relative;
    padding-bottom: 2px;
    color: ${({ theme }) => theme['input-borders']};
    font-size: 13px;
    line-height: 2.2rem;
    font-size: 1.3rem;
`
export const LabelBlockedChannel = styled.div`
    font-size: 11px;
    height: 42px;
    padding: 10px;
    border: 1px solid ${({ theme }) => theme['input-borders']};
    border-radius: 3px;
    .fa-search {
        font-size: 14px;
        right: 21px;
        position: absolute;
        top: 50px;
    }
`

export const ChannelRelativeContainer = styled.div`
    position: relative;
    margin-top: 17px;
    border-top: 1px solid ${({ theme }) => theme['dash-background']};
    flex: 1;
    height: fit-content;
    max-height: 350px;
    && ${FullHeightSelectorOpenContainer} {
        z-index: 101002;
    }
`
const AssetInfo = styled.div`
    font-size: 10px;
    color: ${({ theme }) => theme['gray-dark']};
`

const AssetEmptyState = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    color: ${({ theme }) => theme['auxiliar-typography']};
`
const EmptyStateIconContainer = styled.div`
    margin: 64px 0px 16px 0px;
`

export default channelsData(ChannelsConfig, { updateQuery: true, forceRender: true, confirmation: true })
